import { Option, Options, Select, Value } from 'baseui/select'
import FormPlateInput from 'parkdepot-shared/components/LPInput'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v1 as uuidv1 } from 'uuid'
import { VehicleIdToIcon } from '../../constants/vehicleTypes.consts'
import { useGetUser } from '../../hooks/useAuth'
import { Vehicle } from '../../types/vehicle.interface'
import mapVehiclesWithPlates from '../../utils/mapVehiclesWithPlates.utils'
import {
    FormPlateInputWrapper,
    OptionIcon,
    OptionLabel,
    OptionLabelWrapper,
    SelectArrow,
    SelectOverrides,
    ValueLabelWrapper,
    Wrapper
} from './VehicleSelect.style'
import { LotViewName } from '../../types/lot.interface'
import { CheckoutContext } from '../../context/CheckoutContext/checkout.context'
import NewVehicleDialog from '../NewVehicleDialog/NewVehicleDialog.component'

interface Props {
    vehicle?: Vehicle
    selectedVehicles: Vehicle[]
    hideSelect?: boolean
    setSelectedVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>
}

const vehicleOptionsCONST: Options = [
    {
        id: 1
    }
]

const VehicleSelect: FC<Props> = ({ vehicle, hideSelect = false, selectedVehicles, setSelectedVehicles }) => {
    const { t } = useTranslation()

    const [dropdownValue, setDropdownValue] = useState<Value>([])
    const [vehicleOptions, setVehicleOptions] = useState([...vehicleOptionsCONST])
    const [newVehicle, setNewVehicle] = useState<Vehicle | undefined>(vehicle)
    const [showAddVehicle, setShowAddVehicle] = useState(false)

    const { user } = useGetUser()
    const { checkout } = useContext(CheckoutContext)
    const isSubscription = checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION

    const onDropdownValueChange = (value: Value) => {
        if (value.length === 0) return
        if (value && value[0]?.id === 2) {
            setShowAddVehicle(true)
            return
        }
        if (JSON.stringify(value) !== JSON.stringify(dropdownValue)) {
            setDropdownValue(value)
            const existingVehicles = mapVehiclesWithPlates(user?.vehicles || [], user?.license_plates || [])

            const veh = existingVehicles.find((v: Vehicle) => v.id === value[0].id)
            if (veh) {
                setSelectedVehicles((prev) => {
                    if (prev.length === 0) return [veh]
                    return prev.map((v) => {
                        if (v.id === veh.id || v.id === vehicle?.id) {
                            return veh
                        }
                        return v
                    })
                })
            } else {
                setSelectedVehicles((prev) => {
                    if (prev.length === 0)
                        return [
                            {
                                id: uuidv1(),
                                country: 'DE',
                                new: true
                            }
                        ]
                    return prev.map((v) => {
                        if (v.id === vehicle?.id) {
                            return {
                                id: uuidv1(),
                                country: 'DE',
                                new: true
                            }
                        }
                        return v
                    })
                })
            }
        }
        if (value && value[0]?.id === 1) {
            // setShowAddVehicle(true)
        }
    }

    const onNewVehicleChange = (value: string, type: 'plate' | 'country') => {
        const updatedVehicle = { ...newVehicle, [type]: value }
        setNewVehicle(updatedVehicle)
        setSelectedVehicles((prev) => {
            if (prev.length === 0) return [updatedVehicle]
            return prev.map((v) => {
                if (v.id === updatedVehicle.id || v.id === vehicle?.id) {
                    return updatedVehicle
                }
                return v
            })
        })
    }

    const onNewVehicleSubmit = (vehicle: Vehicle) => {
        const newVehicleOption: Option = {
            id: vehicle.id || uuidv1(),
            label: `${vehicle.name}, ${vehicle.plate}`,
            icon: VehicleIdToIcon(vehicle.vehicle_type || '')
        }

        // Add the new vehicle option to the existing options
        setVehicleOptions((prevOptions) => [...prevOptions, newVehicleOption])

        // Update the dropdown value to select the new vehicle
        setDropdownValue([newVehicleOption])
        setSelectedVehicles([vehicle])
    }

    useEffect(() => {
        if (!user) return
        const { vehicles, license_plates } = user
        const existingVehicles = mapVehiclesWithPlates(vehicles, license_plates)
        const vehicleOptions: Options = existingVehicles.map((vehicle) => ({
            id: vehicle.id,
            label: `${vehicle.name}, ${vehicle.plate}`,
            icon: VehicleIdToIcon(vehicle.vehicle_type || ''),
            disabled: selectedVehicles.some((v) => v.id === vehicle.id)
        }))

        // if checkout type is subscription don't show the different vehicle option
        if (isSubscription) {
            setVehicleOptions([
                ...vehicleOptions,
                ...(existingVehicles.length < 2 ? [{ id: 2, label: t('profile.vehicle.addNewVehicleBtn') }] : [])
            ])
        } else {
            setVehicleOptions([...vehicleOptions, { id: 1, label: t('common.differentVehicle'), new: true }])
        }
    }, [user, selectedVehicles, t])

    useEffect(() => {
        if (hideSelect) return
        if (vehicle && vehicle.id) {
            if (vehicle.new) {
                setDropdownValue([...vehicleOptionsCONST].slice(0, 1))
            } else {
                setDropdownValue([
                    {
                        id: vehicle.id,
                        label: `${vehicle.name}, ${vehicle.plate}`,
                        icon: VehicleIdToIcon(vehicle.vehicle_type || '')
                    }
                ])
            }
        }
    }, [vehicle, hideSelect])

    return (
        <Wrapper>
            {(!hideSelect || isSubscription) && (
                <Select
                    placeholder={t('checkout.selectAVehicle')}
                    options={vehicleOptions}
                    labelKey="label"
                    valueKey="label"
                    searchable={false}
                    clearable={false}
                    onChange={({ value }) => onDropdownValueChange(value)}
                    value={dropdownValue}
                    getOptionLabel={(value) => (
                        <OptionLabelWrapper
                            $primaryColor={
                                dropdownValue && dropdownValue?.length > 0 && dropdownValue[0].id === value?.option.id
                            }
                            $disabled={value?.option?.disabled || false}
                        >
                            {value?.option?.icon && <OptionIcon className={`${value?.option?.icon}`} />}
                            <OptionLabel>{value?.option?.label || t('common.differentVehicle')}</OptionLabel>
                        </OptionLabelWrapper>
                    )}
                    getValueLabel={(value) => (
                        <ValueLabelWrapper>
                            {value?.option?.icon && <OptionIcon className={`${value?.option?.icon}`} />}
                            <OptionLabel>{value?.option?.label || t('common.differentVehicle')}</OptionLabel>
                        </ValueLabelWrapper>
                    )}
                    overrides={{
                        ...SelectOverrides,
                        SelectArrow: {
                            component: () => <SelectArrow className="wemolo-chevron-down" />
                        }
                    }}
                />
            )}
            {((hideSelect && !isSubscription) || vehicle?.new) && (
                <FormPlateInputWrapper>
                    <FormPlateInput
                        value={newVehicle?.plate || ''}
                        onChange={(pl) => onNewVehicleChange(pl, 'plate')}
                        country={newVehicle?.country || 'DE'}
                        onCountryChange={(country) => onNewVehicleChange(country, 'country')}
                    />
                </FormPlateInputWrapper>
            )}
            <NewVehicleDialog isOpen={showAddVehicle} setIsOpen={setShowAddVehicle} onSubmit={onNewVehicleSubmit} />
        </Wrapper>
    )
}

export default VehicleSelect
